import {
  AccessTimeOutlined,
  BoltOutlined,
  CheckCircleOutline,
  CheckCircleOutlined,
  FileUploadOutlined,
  HourglassEmptyOutlined,
  NotificationsOutlined,
  PeopleOutline,
  PersonAddOutlined,
  TrendingUpOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { CardContainer, MetricCard, Tag } from 'finara-components';

import { ErrorState, LoadingState } from '@/components/common';
import { useOnboardingDashboard } from '@/hooks/queries/useOnboarding';

export const DashboardTab: React.FC = () => {
  const { data: dashboardData, isLoading, error } = useOnboardingDashboard();

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'step_completed':
        return <CheckCircleOutline className="text-green-600" />;
      case 'document_uploaded':
        return <FileUploadOutlined className="text-blue-600" />;
      case 'reminder_sent':
        return <NotificationsOutlined className="text-purple-600" />;
      case 'step_started':
        return <PersonAddOutlined className="text-orange-600" />;
      case 'kyc_verified':
        return <CheckCircleOutline className="text-green-600" />;
      case 'status_changed':
        return <AccessTimeOutlined className="text-blue-600" />;
      default:
        return <AccessTimeOutlined className="text-muted" />;
    }
  };

  const getPriorityVariant = (priority: 'alta' | 'media' | 'baja'): 'red' | 'yellow' | 'green' => {
    switch (priority) {
      case 'alta':
        return 'red';
      case 'media':
        return 'yellow';
      case 'baja':
        return 'green';
      default:
        return 'yellow';
    }
  };

  const getPriorityLabel = (priority: 'alta' | 'media' | 'baja'): string => {
    switch (priority) {
      case 'alta':
        return 'Alta';
      case 'media':
        return 'Media';
      case 'baja':
        return 'Baja';
      default:
        return priority;
    }
  };

  if (isLoading) return <LoadingState message="Cargando métricas de onboarding..." />;
  if (error)
    return (
      <ErrorState title="Error al cargar el dashboard" message="Intenta nuevamente más tarde" />
    );
  if (!dashboardData) return null;

  return (
    <div className="space-y-6">
      {/* Metrics */}
      <div className="grid grid-cols-1 md:!grid-cols-6 gap-6">
        <MetricCard
          title="Total Onboardings"
          value={dashboardData.totalOnboardings.toString()}
          icon={<PeopleOutline className="text-blue-600" />}
          variant="white"
        />
        <MetricCard
          title="Pendientes"
          value={dashboardData.pendientes.toString()}
          icon={<HourglassEmptyOutlined className="text-orange-600" />}
          variant="white"
        />
        <MetricCard
          title="En Proceso"
          value={dashboardData.enProceso.toString()}
          icon={<TrendingUpOutlined className="text-blue-600" />}
          variant="white"
        />
        <MetricCard
          title="Completados"
          value={dashboardData.completados.toString()}
          icon={<CheckCircleOutlined className="text-green-600" />}
          variant="white"
        />
        <MetricCard
          title="Tiempo Promedio"
          value={`${dashboardData.tiempoPromedio.toFixed(1)} días`}
          icon={<AccessTimeOutlined className="text-purple-600" />}
          variant="white"
        />
        <MetricCard
          title="Alta Prioridad"
          value={dashboardData.altaPrioridad.toString()}
          icon={<WarningAmberOutlined className="text-red-600" />}
          variant="white"
        />
      </div>

      <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Requieren Atención Inmediata */}
          <CardContainer>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <BoltOutlined className="text-orange-600" />
                <h3 className="text-lg font-semibold text-brand-white">
                  Requieren Atención Inmediata
                </h3>
              </div>

              {dashboardData.requireAttention.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircleOutlined className="text-muted mb-2" style={{ fontSize: 48 }} />
                  <p className="text-muted">No hay onboardings que requieran atención</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.requireAttention.map(item => (
                    <div
                      key={item.userId}
                      className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-muted">
                            {item.name
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                              .substring(0, 2)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-brand-white">{item.name}</p>
                          <p className="text-sm text-muted">{item.jobTitle}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Tag variant={getPriorityVariant(item.priority)}>
                          {getPriorityLabel(item.priority)}
                        </Tag>
                        <p className="text-xs text-muted mt-1">{item.daysInProcess} días</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContainer>

          {/* Actividad Reciente */}
          <CardContainer>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6">
                <AccessTimeOutlined className="text-muted" />
                <h3 className="text-lg font-semibold text-brand-white">Actividad Reciente</h3>
              </div>

              {dashboardData.recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <AccessTimeOutlined className="text-muted mb-2" style={{ fontSize: 48 }} />
                  <p className="text-muted">No hay actividad reciente</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.recentActivity.map(activity => (
                    <div key={activity.id} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">{getActivityIcon(activity.type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-brand-white">{activity.description}</p>
                        <p className="text-xs text-muted mt-1">{activity.timeAgo}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContainer>
        </div>

        {/* Right Column - Progreso General */}
        <CardContainer>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUpOutlined className="text-muted" />
              <h3 className="text-lg font-semibold text-brand-white">Progreso General</h3>
            </div>

            {dashboardData.generalProgress.length === 0 ? (
              <div className="text-center py-8">
                <PeopleOutline className="text-muted mb-2" style={{ fontSize: 48 }} />
                <p className="text-muted">No hay onboardings en proceso</p>
              </div>
            ) : (
              <div className="space-y-6">
                {dashboardData.generalProgress.map(item => (
                  <div key={item.userId}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-brand-white">{item.name}</p>
                        <p className="text-sm text-muted">{item.currentStep}</p>
                      </div>
                      <span className="text-lg font-semibold text-brand-white">{item.progress}%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div
                        className="bg-brand-blue rounded-full h-2 transition-all duration-300"
                        style={{ width: `${item.progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContainer>
      </div>
    </div>
  );
};
